
import { withAuthRetry } from '../utils/authRetry';
import { getServiceBaseUrl } from '../utils/api';

// Simple interface for ReviewerCase until proto is generated
export interface ReviewerCase {
    id: string;
    cancerType: number;
    submitTime?: Date;
    status: number;
    aiJobStartTime?: Date;
    aiJobEndTime?: Date;
    verbosity?: number;
    reasoning?: number;
    finalModel?: string;
}

export interface GetReviewerCasesRequest {
    offset: number;
    pageSize: number;
    filter: 'to-review' | 'reviewed';
}

export interface GetReviewerCasesResponse {
    cases: ReviewerCase[];
    totalCount: number;
}

// Manual decoder for GetReviewerCasesResponse until proto is auto-generated
function decodeGetReviewerCasesResponse(bytes: Uint8Array): GetReviewerCasesResponse {
    // Import the generated decoder when available
    // For now, implement a simple protobuf decoder
    const { BinaryReader } = require("@bufbuild/protobuf/wire");
    const reader = new BinaryReader(bytes);

    const response: GetReviewerCasesResponse = { cases: [], totalCount: 0 };

    while (reader.pos < reader.len) {
        const tag = reader.uint32();
        const fieldNum = tag >>> 3;

        switch (fieldNum) {
            case 1: { // cases (repeated ReviewerCase)
                const caseBytes = reader.bytes();
                const caseReader = new BinaryReader(caseBytes);
                const reviewerCase: ReviewerCase = {
                    id: '',
                    cancerType: 0,
                    status: 0,
                };

                while (caseReader.pos < caseReader.len) {
                    const caseTag = caseReader.uint32();
                    const caseFieldNum = caseTag >>> 3;

                    switch (caseFieldNum) {
                        case 1: reviewerCase.id = caseReader.string(); break;
                        case 2: reviewerCase.cancerType = caseReader.int32(); break;
                        case 3: {
                            // Timestamp - need to read as submessage
                            const tsBytes = caseReader.bytes();
                            const tsReader = new BinaryReader(tsBytes);
                            let seconds = 0;
                            let nanos = 0;
                            while (tsReader.pos < tsReader.len) {
                                const tsTag = tsReader.uint32();
                                const tsField = tsTag >>> 3;
                                if (tsField === 1) seconds = Number(tsReader.int64());
                                else if (tsField === 2) nanos = tsReader.int32();
                                else tsReader.skip(tsTag & 7);
                            }
                            reviewerCase.submitTime = new Date(seconds * 1000 + nanos / 1000000);
                            break;
                        }
                        case 4: reviewerCase.status = caseReader.int32(); break;
                        case 5: {
                            const tsBytes = caseReader.bytes();
                            const tsReader = new BinaryReader(tsBytes);
                            let seconds = 0;
                            let nanos = 0;
                            while (tsReader.pos < tsReader.len) {
                                const tsTag = tsReader.uint32();
                                const tsField = tsTag >>> 3;
                                if (tsField === 1) seconds = Number(tsReader.int64());
                                else if (tsField === 2) nanos = tsReader.int32();
                                else tsReader.skip(tsTag & 7);
                            }
                            reviewerCase.aiJobStartTime = new Date(seconds * 1000 + nanos / 1000000);
                            break;
                        }
                        case 6: {
                            const tsBytes = caseReader.bytes();
                            const tsReader = new BinaryReader(tsBytes);
                            let seconds = 0;
                            let nanos = 0;
                            while (tsReader.pos < tsReader.len) {
                                const tsTag = tsReader.uint32();
                                const tsField = tsTag >>> 3;
                                if (tsField === 1) seconds = Number(tsReader.int64());
                                else if (tsField === 2) nanos = tsReader.int32();
                                else tsReader.skip(tsTag & 7);
                            }
                            reviewerCase.aiJobEndTime = new Date(seconds * 1000 + nanos / 1000000);
                            break;
                        }
                        case 7: reviewerCase.verbosity = caseReader.int32(); break;
                        case 8: reviewerCase.reasoning = caseReader.int32(); break;
                        case 9: reviewerCase.finalModel = caseReader.string(); break;
                        default: caseReader.skip(caseTag & 7);
                    }
                }
                response.cases.push(reviewerCase);
                break;
            }
            case 2: { // total_count
                response.totalCount = Number(reader.int64());
                break;
            }
            default:
                reader.skip(tag & 7);
        }
    }

    return response;
}

export async function getReviewerCases(
    request: GetReviewerCasesRequest,
    accessToken: string,
    setAccessToken: (token: string | null) => void,
    logout: () => void
): Promise<GetReviewerCasesResponse> {
    const originalApiCall = async (token: string): Promise<Response> => {
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + token
        };

        const { baseUrl, stagingSuffix } = getServiceBaseUrl('reviewservice');
        const url = `${baseUrl}/getreviewercases${stagingSuffix}?offset=${request.offset}&page_size=${request.pageSize}&filter=${request.filter}`;

        const response = await fetch(url, {
            method: 'GET',
            headers,
        });
        return response;
    };

    return withAuthRetry(originalApiCall, accessToken, decodeGetReviewerCasesResponse, setAccessToken, logout);
}
